/**
 * Reports Configuration Page
 *
 * Platform admin page for managing AI model configuration and viewing prompts.
 * Models are editable, prompts are read-only (for now).
 */

import React, { useEffect, useState } from 'react';
import { get, put, post } from '../../../services/api';
import type {
  AIModelsConfig,
  ReportPrompts,
  CacheClearResponse,
  ConfigUpdateRequest,
} from '../../../types/adminConfig';
import './ReportsConfig.css';

// Section display order
const SECTION_ORDER = [
  'solvencia',
  'liquidez',
  'calidad_activos',
  'eficiencia_gestion',
  'rentabilidad',
  'riesgo_interes',
  'activo',
  'pasivo',
  'patrimonio',
];

export const ReportsConfig: React.FC = () => {
  // State
  const [config, setConfig] = useState<AIModelsConfig | null>(null);
  const [prompts, setPrompts] = useState<ReportPrompts | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state for editing
  const [formData, setFormData] = useState<ConfigUpdateRequest>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [configData, promptsData] = await Promise.all([
        get<AIModelsConfig>('/api/admin/reports-config'),
        get<ReportPrompts>('/api/admin/reports-prompts'),
      ]);

      setConfig(configData);
      setPrompts(promptsData);

      // Initialize form data
      setFormData({
        report_primary_model: configData.report_primary_model,
        report_fallback_model: configData.report_fallback_model,
        chat_model: configData.chat_model,
        insights_model: configData.insights_model,
      });
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading config:', err);
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = (field: keyof ConfigUpdateRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const response = await put<{ success: boolean; config: AIModelsConfig; allowed_models: string[] }>(
        '/api/admin/reports-config',
        formData
      );

      // Merge allowed_models into config (backend returns it at root level)
      setConfig({
        ...response.config,
        allowed_models: response.allowed_models,
      });
      setHasChanges(false);
      setSuccessMessage('Configuration saved successfully. Click "Clear Cache" to apply changes.');
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleClearCache = async () => {
    try {
      setClearingCache(true);
      setError(null);
      setSuccessMessage(null);

      const response = await post<CacheClearResponse>('/api/admin/reports-config/clear-cache');

      if (response.success) {
        const validation = response.validation;
        if (validation.config_valid && validation.prompts_valid) {
          setSuccessMessage(
            `Cache cleared and reloaded successfully. Config valid: Yes. Prompts version: ${response.prompts.version}`
          );
        } else {
          setSuccessMessage(
            `Cache cleared but validation issues: Config: ${validation.config_valid ? 'OK' : 'Invalid'}, Prompts: ${validation.prompts_valid ? 'OK' : 'Invalid'}`
          );
        }

        // Reload data to show fresh values
        await loadData();
      }
    } catch (err) {
      console.error('Error clearing cache:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear cache');
    } finally {
      setClearingCache(false);
    }
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const countLines = (text: string | null): number => {
    if (!text) return 0;
    return text.split('\n').length;
  };

  if (loading) {
    return (
      <div className="reports-config-page">
        <h1>AI & Reports Configuration</h1>
        <div className="loading">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="reports-config-page">
      <h1>AI & Reports Configuration</h1>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* AI Model Settings Card */}
      <div className="config-card">
        <div className="card-header">
          <h2>AI Model Settings</h2>
          <div className="card-actions">
            <button
              className="btn btn-secondary"
              onClick={handleClearCache}
              disabled={clearingCache}
            >
              {clearingCache ? 'Clearing...' : 'Clear Cache'}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        <div className="model-sections">
          {/* Report Generation */}
          <div className="model-section">
            <h3>Report Generation</h3>
            <div className="model-fields">
              <div className="field">
                <label>Primary Model</label>
                <select
                  value={formData.report_primary_model || ''}
                  onChange={(e) => handleModelChange('report_primary_model', e.target.value)}
                >
                  {config?.allowed_models.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Fallback Model</label>
                <select
                  value={formData.report_fallback_model || ''}
                  onChange={(e) => handleModelChange('report_fallback_model', e.target.value)}
                >
                  {config?.allowed_models.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* AI Chat */}
          <div className="model-section">
            <h3>AI Chat</h3>
            <div className="model-fields">
              <div className="field">
                <label>Chat Model</label>
                <select
                  value={formData.chat_model || ''}
                  onChange={(e) => handleModelChange('chat_model', e.target.value)}
                >
                  {config?.allowed_models.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Insights */}
          <div className="model-section">
            <h3>Insights</h3>
            <div className="model-fields">
              <div className="field">
                <label>Insights Model</label>
                <select
                  value={formData.insights_model || ''}
                  onChange={(e) => handleModelChange('insights_model', e.target.value)}
                >
                  {config?.allowed_models.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="config-meta">
          <p>
            <strong>Last updated:</strong> {formatDate(config?.updated_at || null)}
            {config?.updated_by && ` by ${config.updated_by}`}
          </p>
          <p className="cache-note">
            Config and prompts are cached for 1 week. Clear cache after making changes to apply
            immediately.
          </p>
        </div>
      </div>

      {/* Report Section Prompts Card */}
      <div className="config-card">
        <div className="card-header">
          <h2>Report Section Prompts</h2>
          <span className="version-badge">Version {prompts?.version || 0}</span>
        </div>

        <p className="prompts-meta">
          Created: {formatDate(prompts?.created_at || null)} by {prompts?.created_by || 'system'}
        </p>

        <div className="prompts-list">
          {/* System Instruction */}
          <div className="prompt-section">
            <button
              className={`prompt-header ${expandedSections.has('system_instruction') ? 'expanded' : ''}`}
              onClick={() => toggleSection('system_instruction')}
            >
              <span className="expand-icon">{expandedSections.has('system_instruction') ? '▼' : '▶'}</span>
              <span className="prompt-title">System Instruction</span>
              <span className="prompt-lines">
                ({countLines(prompts?.report_system_instruction || null)} lines)
              </span>
            </button>
            {expandedSections.has('system_instruction') && (
              <div className="prompt-content">
                <pre>{prompts?.report_system_instruction || 'Not available'}</pre>
              </div>
            )}
          </div>

          {/* Section Prompts */}
          {SECTION_ORDER.map((key) => {
            const section = prompts?.report_sections?.[key];
            if (!section) return null;

            return (
              <div key={key} className="prompt-section">
                <button
                  className={`prompt-header ${expandedSections.has(key) ? 'expanded' : ''}`}
                  onClick={() => toggleSection(key)}
                >
                  <span className="expand-icon">{expandedSections.has(key) ? '▼' : '▶'}</span>
                  <span className="prompt-title">
                    {section.title_en} ({section.title_es})
                  </span>
                  <span className="prompt-lines">({countLines(section.prompt)} lines)</span>
                </button>
                {expandedSections.has(key) && (
                  <div className="prompt-content">
                    <pre>{section.prompt}</pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="readonly-note">Prompts are read-only. Editing coming soon.</p>
      </div>

      {/* Other AI Prompts Card */}
      <div className="config-card">
        <div className="card-header">
          <h2>Other AI Prompts</h2>
        </div>

        <div className="prompts-list">
          {/* Chat System Instruction */}
          <div className="prompt-section">
            <button
              className={`prompt-header ${expandedSections.has('chat_instruction') ? 'expanded' : ''}`}
              onClick={() => toggleSection('chat_instruction')}
            >
              <span className="expand-icon">{expandedSections.has('chat_instruction') ? '▼' : '▶'}</span>
              <span className="prompt-title">AI Chat System Instruction</span>
              <span className="prompt-lines">
                ({countLines(prompts?.chat_system_instruction || null)} lines)
              </span>
            </button>
            {expandedSections.has('chat_instruction') && (
              <div className="prompt-content">
                <pre>{prompts?.chat_system_instruction || 'Not available'}</pre>
              </div>
            )}
          </div>

          {/* Insights Prompt */}
          {prompts?.insights_prompt_template && (
            <div className="prompt-section">
              <button
                className={`prompt-header ${expandedSections.has('insights_prompt') ? 'expanded' : ''}`}
                onClick={() => toggleSection('insights_prompt')}
              >
                <span className="expand-icon">{expandedSections.has('insights_prompt') ? '▼' : '▶'}</span>
                <span className="prompt-title">Insights Generation Prompt</span>
                <span className="prompt-lines">
                  ({countLines(prompts?.insights_prompt_template)} lines)
                </span>
              </button>
              {expandedSections.has('insights_prompt') && (
                <div className="prompt-content">
                  <pre>{prompts.insights_prompt_template}</pre>
                </div>
              )}
            </div>
          )}

          {/* Preflight System Instruction */}
          <div className="prompt-section">
            <button
              className={`prompt-header ${expandedSections.has('preflight_instruction') ? 'expanded' : ''}`}
              onClick={() => toggleSection('preflight_instruction')}
            >
              <span className="expand-icon">{expandedSections.has('preflight_instruction') ? '▼' : '▶'}</span>
              <span className="prompt-title">Preflight System Instruction</span>
              <span className="prompt-lines">
                ({countLines(prompts?.preflight_system_instruction || null)} lines)
              </span>
            </button>
            {expandedSections.has('preflight_instruction') && (
              <div className="prompt-content">
                <pre>{prompts?.preflight_system_instruction || 'Not available'}</pre>
              </div>
            )}
          </div>

          {/* Preflight Prompt Template */}
          <div className="prompt-section">
            <button
              className={`prompt-header ${expandedSections.has('preflight_prompt') ? 'expanded' : ''}`}
              onClick={() => toggleSection('preflight_prompt')}
            >
              <span className="expand-icon">{expandedSections.has('preflight_prompt') ? '▼' : '▶'}</span>
              <span className="prompt-title">Preflight Analysis Prompt Template</span>
              <span className="prompt-lines">
                ({countLines(prompts?.preflight_prompt_template || null)} lines)
              </span>
            </button>
            {expandedSections.has('preflight_prompt') && (
              <div className="prompt-content">
                <pre>{prompts?.preflight_prompt_template || 'Not available'}</pre>
              </div>
            )}
          </div>
        </div>

        <p className="readonly-note">These prompts are read-only. Editing coming soon.</p>
      </div>
    </div>
  );
};

export default ReportsConfig;
